/**
 * The direction screen — both the measured and the locked state.
 *
 * Reading order is the product's core bet and must not be rearranged:
 * how it works → where it breaks → what it gives you, and only then the action.
 * Putting the exercise higher gets it tapped without understanding why.
 */
import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../i18n/LangContext';
import { formatDate } from '../i18n/detectLanguage';
import { useT, useTt } from '../i18n/useT';
import { AppShell, DeskRow } from './AppShell';
import { useAppContent } from './content';
import { useProfile, PatState } from './profile';
import { Icon } from './icons';
import { Orb } from './orb';
import { XpTag } from './XpTag';
import {
    Acc,
    Appear,
    Body,
    Btn,
    Chip,
    Col,
    Eyebrow,
    Fluid,
    Hint,
    Meta,
    Muted,
    Panel,
    PatLegend,
    PatternRow,
    PolTag,
    Rail,
    Row,
    Sec,
    SegBarC,
    TrendWeek,
    ZoneLevels,
    useIsDesktop,
} from './primitives';
import {
    c,
    hair,
    hair2,
    f,
    ACT,
    scoreColor,
    patternZone,
    effectiveStrength,
    strengthKey,
    LOAD,
    DESKTOP,
    DAY_KEYS,
} from './tokens';
import { R, QUIZ } from './routes';
import { todayIndex } from './tasks';
import { levelOf } from './points';

/** Push-screen header. Carries a lateral exit, so the screen is never a dead end. */
const NavHeader: React.FC<{ title: string; sub?: string; hex?: string; onBack: () => void }> = ({
    title,
    sub,
    hex,
    onBack,
}) => {
    const t = useT();

    return (
        <Row style={{ padding: '14px 16px 12px', gap: 10, borderBottom: hair }}>
            <button
                type="button"
                aria-label={t('app.action.back')}
            onClick={onBack}
            style={{
                width: 34,
                height: 34,
                borderRadius: '50%',
                border: hair,
                background: c.card,
                display: 'grid',
                placeItems: 'center',
                flex: '0 0 auto',
                cursor: 'pointer',
            }}
        >
            <Icon k="back" size={15} color={c.textPri} />
        </button>
            <Col style={{ flex: 1, alignItems: 'center' }} gap={3}>
                <Meta style={{ color: c.textPri, letterSpacing: '0.18em' }}>{title}</Meta>
                {sub && <Meta style={{ fontSize: 10, color: hex || c.muted, letterSpacing: '0.12em' }}>{sub}</Meta>}
            </Col>
            <div style={{ width: 34, flex: '0 0 auto' }} />
        </Row>
    );
};

const DirectionPage: React.FC = () => {
    const { code = '' } = useParams();
    const navigate = useNavigate();
    const { user, userProfile } = useAuth();
    const { lang } = useLang();
    const { t, tn } = useTt();
    const { content } = useAppContent();
    const profile = useProfile(content, user?.id, lang);
    const desktop = useIsDesktop(DESKTOP);

    const d = content?.dirBy[code];
    const st = profile.directions.find((x) => x.code === code);
    const deep = !!st?.deep;
    const score = st?.score ?? null;
    const z = scoreColor(deep ? score : null);

    const back = React.useCallback(() => navigate(R.dirs), [navigate]);

    if (!content || !d) {
        return (
            <AppShell active="dirs" title={t('app.dir.title')}>
                <Col style={{ padding: '60px 20px', alignItems: 'center' }} gap={10}>
                    <Meta style={{ color: c.subtle }}>
                        {t(content ? 'app.dir.notFound' : 'app.state.loading')}
                    </Meta>
                    {content && <Btn onClick={back}>{t('app.dir.backToList')}</Btn>}
                </Col>
            </AppShell>
        );
    }

    const pats: PatState[] = st?.patterns || [];
    const lever = pats.find((p) => p.pol === 'shadow') || pats[0];
    const leverPattern = lever ? content.patBy[lever.code] : null;
    const leverEx =
        (lever && (content.exsByPattern[lever.code] || [])[0]) ||
        content.exercises.find((e) => e.track === code) ||
        null;
    const scoreOf = (dir: string) => profile.directions.find((x) => x.code === dir)?.score ?? null;

    const shell = (children: React.ReactNode) => (
        <AppShell
            active="dirs"
            title={d.disp}
            subtitle={deep ? t('app.dir.updatedToday.zone', { zone: t(z.key) }) : t('app.dir.notTaken')}
            index={profile.index}
            email={userProfile?.email || user?.email}
            mobileHeader={
                !desktop ? (
                    <NavHeader
                        title={d.disp}
                        sub={t(deep ? 'app.dir.updatedToday' : 'app.zone.locked')}
                        hex={deep ? z.hex : undefined}
                        onBack={back}
                    />
                ) : undefined
            }
            chips={
                <Link to={R.dirs} style={{ textDecoration: 'none' }}>
                    <Meta style={{ fontSize: 10, border: hair, borderRadius: 99, padding: '6px 10px' }}>
                        {t('app.dir.allDirections')}
                    </Meta>
                </Link>
            }
        >
            {children}
        </AppShell>
    );

    /* ── locked direction ────────────────────────────── */
    if (!deep) {
        return shell(
            // A locked direction is a short read, so desktop clamps it to a
            // column instead of stretching six lines across the whole page.
            <Appear style={desktop ? { maxWidth: 620, margin: '0 auto', padding: '10px 0 40px' } : undefined}>
                <div style={{ paddingTop: 22 }}>
                    <Orb empty label={d.disp.toUpperCase()} size={172} />
                    <Row
                        style={{ justifyContent: 'center', gap: 8, marginTop: 14, padding: '0 20px', flexWrap: 'wrap' }}
                    >
                        <Chip>{t('app.dir.notTaken')}</Chip>
                        <Chip color={c.subtle}>{t('app.dir.sixMin')}</Chip>
                        {!!st?.earned && (
                            <Chip color={ACT} border={`${ACT}44`}>
                                {t('app.dir.earned', { n: st.earned })}
                            </Chip>
                        )}
                    </Row>
                    <Body style={{ padding: '16px 22px 0', textAlign: 'center' }}>{d.sub}</Body>
                </div>

                <Sec title={t('app.dir.measures')} style={{ paddingTop: 22 }}>
                    <Col gap={9}>
                        {d.measure.slice(0, 5).map((line, i) => (
                            <Row key={line} style={{ gap: 11, alignItems: 'flex-start' }}>
                                <Meta style={{ fontSize: 10, color: c.subtle, marginTop: 3, flex: '0 0 auto' }}>
                                    {String(i + 1).padStart(2, '0')}
                                </Meta>
                                <Body style={{ fontSize: 13 }}>{line}</Body>
                            </Row>
                        ))}
                    </Col>
                </Sec>

                <Sec title={t('app.dir.benefits')} bt style={{ marginTop: 20 }}>
                    <Col gap={9}>
                        {d.benefits.map((line) => (
                            <Row key={line} style={{ gap: 11, alignItems: 'flex-start' }}>
                                <Icon
                                    k="check"
                                    size={13}
                                    color="rgba(232,230,224,0.4)"
                                    style={{ marginTop: 3, flex: '0 0 auto' }}
                                />
                                <Muted style={{ fontSize: 13 }}>{line}</Muted>
                            </Row>
                        ))}
                    </Col>
                    <Row
                        style={{
                            gap: 9,
                            marginTop: 16,
                            padding: '12px 13px',
                            border: hair,
                            borderRadius: 13,
                            background: 'rgba(232,230,224,0.015)',
                        }}
                    >
                        <Icon k="lock" size={15} color={c.subtle} />
                        <Muted style={{ fontSize: 12.5, flex: 1 }}>
                            {tn('app.dir.hiddenPatterns', (content.patsByDim[code] || []).length)}
                        </Muted>
                    </Row>
                </Sec>

                <div style={{ padding: '22px 20px 26px', maxWidth: 420 }}>
                    <Btn primary ic="arrow" onClick={() => navigate(QUIZ.track(code))}>
                        {t('app.dir.takeTest')}
                    </Btn>
                </div>
            </Appear>,
        );
    }

    /* ── measured direction ──────────────────────────── */
    const reading = (
        <>
            <Acc title={t('app.dir.why')} meta={t('app.dir.why.meta')} open hex={z.hex}>
                <Col gap={13}>
                    {d.theory.map((p) => (
                        <Body key={p.slice(0, 24)}>{p}</Body>
                    ))}
                </Col>
                {d.research[0] && (
                    <Row
                        style={{
                            gap: 9,
                            marginTop: 14,
                            padding: '11px 13px',
                            background: c.cardIn,
                            borderRadius: 12,
                            border: hair,
                            alignItems: 'flex-start',
                        }}
                    >
                        <Icon k="book" size={14} color={z.hex} style={{ marginTop: 1, flex: '0 0 auto' }} />
                        <Muted style={{ fontSize: 12 }}>
                            {tn('app.dir.researchBelow', d.research.length)}
                        </Muted>
                    </Row>
                )}
            </Acc>

            <Acc title={t('app.dir.traps')} meta={tn('app.dir.traps.meta', d.traps.length)} open hex={z.hex}>
                <div style={desktop ? { columnCount: 2, columnGap: 26 } : undefined}>
                    {d.traps.map((line) => (
                        <Row
                            key={line.slice(0, 24)}
                            style={{ gap: 11, alignItems: 'flex-start', marginBottom: 10, breakInside: 'avoid' }}
                        >
                            <span
                                style={{
                                    width: 5,
                                    height: 5,
                                    borderRadius: '50%',
                                    background: '#fb923c',
                                    marginTop: 7,
                                    flex: '0 0 auto',
                                }}
                            />
                            <Body style={{ fontSize: 13.5 }}>{line}</Body>
                        </Row>
                    ))}
                </div>
            </Acc>

            <Acc title={t('app.dir.helps')} meta={tn('app.dir.helps.meta', d.helps.length)} open hex={z.hex}>
                <Col gap={10}>
                    {d.helps.map((line) => (
                        <Row key={line.slice(0, 24)} style={{ gap: 11, alignItems: 'flex-start' }}>
                            <Icon k="check" size={13} color="#4ade80" style={{ marginTop: 3, flex: '0 0 auto' }} />
                            <Body style={{ fontSize: 13.5 }}>{line}</Body>
                        </Row>
                    ))}
                </Col>
            </Acc>

            <Acc title={t('app.dir.research')} meta={tn('app.dir.research.meta', d.research.length)} hex={z.hex} last>
                <Col gap={14}>
                    {d.research.map(([claim, src]) => (
                        <Col key={src} gap={6}>
                            <Body style={{ fontSize: 13.5 }}>{claim}</Body>
                            <Meta
                                style={{
                                    fontSize: 10,
                                    color: c.subtle,
                                    letterSpacing: '0.06em',
                                    textTransform: 'none',
                                }}
                            >
                                {src}
                            </Meta>
                        </Col>
                    ))}
                </Col>
            </Acc>
        </>
    );

    // The direction's own base/tasks split plus its level bar — the same stack
    // the main screen shows, so the two read as one system. The level grows
    // from the points tasks have paid into THIS direction.
    const lv = levelOf(st?.earned || 0);

    const dirHonesty = (
        <Col gap={11}>
            <Row style={{ border: hair, borderRadius: 14, overflow: 'hidden', alignItems: 'stretch' }}>
                <Col
                    style={{ flex: 1, minWidth: 0, padding: '11px 13px', background: 'rgba(232,230,224,0.015)' }}
                    gap={4}
                >
                    <Row style={{ gap: 7 }}>
                        <Meta style={{ fontSize: 9.5, letterSpacing: '0.12em' }}>{t('app.state.base')}</Meta>
                        <span style={{ fontFamily: f.mono, fontSize: 14, color: c.textPri, fontWeight: 300 }}>
                            {score ?? '–'}
                        </span>
                    </Row>
                    <span style={{ fontSize: 10, color: c.subtle, lineHeight: 1.3 }}>
                        {st?.testedAt
                            ? t('app.state.base.from', {
                                  date: formatDate(lang, st.testedAt, { day: 'numeric', month: 'long' }),
                              })
                            : t('app.state.base.fromLast')}
                    </span>
                    <Link to={QUIZ.track(code)} style={{ textDecoration: 'none' }}>
                        <Row style={{ gap: 5, marginTop: 2 }}>
                            <Meta style={{ fontSize: 9, color: c.text, letterSpacing: '0.1em' }}>
                                {t('app.state.retake')}
                            </Meta>
                            <Icon k="arrow" size={11} color={c.text} />
                        </Row>
                    </Link>
                </Col>
                <Col style={{ flex: 1, minWidth: 0, padding: '11px 13px', borderLeft: hair }} gap={4}>
                    <Row style={{ gap: 7 }}>
                        <Meta style={{ fontSize: 9.5, letterSpacing: '0.12em' }}>{t('app.state.dynamic')}</Meta>
                        <span
                            style={{
                                fontFamily: f.mono,
                                fontSize: 14,
                                color: st?.earned ? ACT : c.dim,
                                fontWeight: 300,
                            }}
                        >
                            {st?.earned || 0}
                        </span>
                    </Row>
                    <span style={{ fontSize: 10, color: c.subtle, lineHeight: 1.3 }}>
                        {t(st?.earned ? 'app.state.dynamic.sum' : 'app.state.dynamic.empty')}
                    </span>
                </Col>
            </Row>
            <Row style={{ gap: 7 }}>
                <Hint text={t('app.state.level.hint')}>
                    <Meta
                        style={{
                            fontSize: 9.5,
                            color: st?.earned ? c.text : c.subtle,
                            letterSpacing: '0.1em',
                            borderBottom: '1px dotted rgba(232,230,224,0.3)',
                            cursor: 'help',
                        }}
                    >
                        {t('app.state.level', { level: lv.level })}
                    </Meta>
                </Hint>
                <div style={{ flex: 1, height: 3, borderRadius: 2, background: c.track, overflow: 'hidden' }}>
                    <div
                        style={{
                            width: `${Math.round(lv.pct * 100)}%`,
                            height: '100%',
                            background: ACT,
                            transition: 'width 420ms ease',
                        }}
                    />
                </div>
            </Row>
        </Col>
    );

    const hero = (
        <div style={{ position: 'relative', paddingTop: 22 }}>
            <div
                style={{
                    position: 'absolute',
                    top: -30,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: 420,
                    height: 250,
                    background: `radial-gradient(ellipse at 50% 0%, ${z.hex}18, transparent 62%)`,
                    pointerEvents: 'none',
                }}
            />
            <Orb score={score} label={d.disp.toUpperCase()} size={196} />
            {score != null && <ZoneLevels score={score} style={{ marginTop: 16 }} />}
            <div style={{ margin: '16px 20px 0' }}>{dirHonesty}</div>
            <Body style={{ padding: '18px 22px 20px', textAlign: 'center' }}>{d.sub}</Body>
        </div>
    );

    const patternsBlock = (
        <Sec
            title={t('app.dir.patterns')}
            right={<Meta style={{ fontSize: 10 }}>{t('app.dir.patternsFound', { n: pats.length })}</Meta>}
        >
            {pats.length ? (
                <>
                    <Col gap={0}>
                        {pats.map((m, i) => (
                            <div key={m.code} style={{ borderTop: i ? '1px solid rgba(232,230,224,0.04)' : 'none' }}>
                                <PatternRow
                                    name={m.name}
                                    sub={m.sub}
                                    pol={m.pol}
                                    pct={m.pct}
                                    earned={m.earned}
                                    isNew={m.isNew}
                                />
                            </div>
                        ))}
                    </Col>
                    <PatLegend />
                </>
            ) : (
                <Muted style={{ fontSize: 12.5 }}>{t('app.dir.patterns.none')}</Muted>
            )}
        </Sec>
    );

    // Extracted so the mobile card and the desktop rail panel stay one source.
    //
    // The name gets its own line rather than being glued into a sentence. Pattern
    // names in this model are themselves clauses — "The good doesn't count",
    // "I dissolve into closeness" — so "<name> drags this direction" reads as a
    // broken phrase no amount of bolding fixes. Subject on one line, verdict under.
    const leverZone = lever && lever.pct != null ? patternZone(lever.pct, lever.pol) : z;
    const leverVerdict = t(leverPattern?.pol === 'engine' ? 'app.dir.lever.engine' : 'app.dir.lever.shadow');

    const leverInner = leverPattern && leverEx && (
        <>
            <Row style={{ gap: 10, marginTop: 4 }}>
                <span
                    style={{
                        width: 7,
                        height: 7,
                        borderRadius: '50%',
                        flex: '0 0 auto',
                        background: leverZone.hex,
                        boxShadow: `0 0 7px ${leverZone.hex}`,
                    }}
                />
                <span
                    style={{
                        fontSize: 16.5,
                        color: c.textPri,
                        fontWeight: 500,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.25,
                        flex: 1,
                        minWidth: 0,
                    }}
                >
                    {leverPattern.name}
                </span>
                {lever?.pct != null && (
                    <span style={{ fontFamily: f.mono, fontSize: 15, fontWeight: 300, color: c.textPri }}>
                        {Math.round(lever.pct)}
                    </span>
                )}
            </Row>
            {lever?.pct != null && (
                <div style={{ marginTop: 10, marginLeft: 17 }}>
                    <SegBarC pct={effectiveStrength(lever.pct, lever.pol)} hex={leverZone.hex} n={12} />
                </div>
            )}
            <Muted style={{ fontSize: 12.5, marginTop: 9, marginLeft: 17 }}>{leverVerdict}</Muted>
            <Muted style={{ marginTop: 14, fontSize: 13, fontFamily: f.serif, fontStyle: 'italic', lineHeight: 1.38 }}>
                {leverPattern.aha}
            </Muted>
            <Link to={R.task(leverEx.code)} style={{ textDecoration: 'none' }}>
                <Row
                    style={{
                        gap: 10,
                        marginTop: 14,
                        padding: '11px 13px',
                        background: c.cardIn,
                        border: hair,
                        borderRadius: 13,
                    }}
                >
                    <Icon k="report" size={16} color={z.hex} />
                    <Col style={{ flex: 1 }} gap={3}>
                        <span style={{ fontSize: 13.5, color: c.textPri, fontWeight: 400 }}>{leverEx.name}</span>
                        <Meta style={{ fontSize: 9.5, letterSpacing: '0.08em' }}>
                            {leverEx.min ? `${t('app.task.min', { n: leverEx.min })} · ` : ''}
                            {t(LOAD[leverEx.load] || leverEx.load)}
                        </Meta>
                        <XpTag ex={leverEx} content={content} scoreOf={scoreOf} style={{ marginTop: 2 }} />
                    </Col>
                    <Icon k="chev" size={13} color={c.subtle} />
                </Row>
            </Link>
        </>
    );

    const leverBlock = leverInner && (
        <div style={{ padding: '22px 20px 26px' }}>
            <div
                style={{
                    border: `1px solid ${z.hex}33`,
                    background: `linear-gradient(180deg, ${z.hex}0d, ${c.card} 62%)`,
                    borderRadius: 17,
                    padding: '15px 16px',
                }}
            >
                <Eyebrow color={z.hex} style={{ marginBottom: 9 }}>
                    {t('app.dir.lever')}
                </Eyebrow>
                {leverInner}
            </div>
        </div>
    );

    const leverPanel = leverInner && (
        <Panel
            title={t('app.dir.lever')}
            pad="16px 18px 18px"
            style={{ borderColor: `${z.hex}33`, background: `linear-gradient(180deg, ${z.hex}0d, ${c.card} 62%)` }}
        >
            {leverInner}
        </Panel>
    );

    // The bars are points completed tasks paid into THIS direction, day by day —
    // not the measured score. The score only moves on a retake (see `profile.ts`),
    // so charting it daily would draw a flat line and call it a trend.
    const hasTrend = !!st && st.trend.length === 7 && st.earned > 0;

    const trend = hasTrend && st && (
        <TrendWeek
            data={st.trend}
            todayIdx={todayIndex()}
            hex={ACT}
            footL={`${t('app.state.average')} ${tn('app.state.points', Math.round(st.trend.reduce((a, b) => a + b, 0) / 7))}`}
            footR={t('app.dir.earned', { n: st.earned })}
        />
    );

    /** No reports yet — the slot still exists, it just says so. */
    const trendEmpty = (
        <>
            <Row style={{ gap: 7, alignItems: 'flex-end', opacity: 0.5 }}>
                {DAY_KEYS.map((key, i) => (
                    <Col key={key} style={{ flex: 1, alignItems: 'center' }} gap={6}>
                        <div style={{ width: '100%', height: 46, display: 'flex', alignItems: 'flex-end' }}>
                            <div
                                style={{
                                    width: '100%',
                                    height: i === todayIndex() ? 6 : 3,
                                    borderRadius: 3,
                                    background: 'rgba(232,230,224,0.08)',
                                }}
                            />
                        </div>
                        <span style={{ fontFamily: f.mono, fontSize: 9, color: c.subtle }}>{t(key)}</span>
                    </Col>
                ))}
            </Row>
            <Muted style={{ fontSize: 12, marginTop: 12 }}>{t('app.dir.trend.empty')}</Muted>
        </>
    );

    const trendBlock = <Sec title={t('app.dir.trend')}>{trend || trendEmpty}</Sec>;
    const trendPanel = (
        <Panel
            title={t('app.dir.trend')}
            right={
                hasTrend ? undefined : (
                    <Meta style={{ fontSize: 9.5, color: c.subtle }}>{t('app.pattern.collecting')}</Meta>
                )
            }
        >
            {trend || trendEmpty}
        </Panel>
    );

    if (desktop) {
        return shell(
            <Appear>
                <DeskRow>
                    <Rail width={340}>
                        <Panel pad="24px 22px 20px">
                            <Orb score={score} label={d.disp.toUpperCase()} size={188} />
                            {score != null && (
                                <div style={{ marginTop: 18, marginLeft: -20, marginRight: -20 }}>
                                    <ZoneLevels score={score} />
                                </div>
                            )}
                            <div style={{ marginTop: 16 }}>{dirHonesty}</div>
                            <Body style={{ fontSize: 13.5, marginTop: 18, textAlign: 'center' }}>{d.sub}</Body>
                        </Panel>
                        {trendPanel}
                    </Rail>

                    <Fluid>
                        <Panel pad="4px 24px 8px">{reading}</Panel>
                        <Panel
                            title={t('app.dir.patterns')}
                            right={
                                <Meta style={{ fontSize: 9.5 }}>
                                    {t('app.dir.patternsFound', { n: pats.length })}
                                </Meta>
                            }
                            pad="6px 22px 18px"
                        >
                            {pats.length ? (
                                <>
                                    {pats.map((m, i) => {
                                        const pz = patternZone(m.pct as number, m.pol);

                                        return (
                                            <Row
                                                key={m.code}
                                                style={{ gap: 20, padding: '15px 0', borderTop: i ? hair2 : 'none' }}
                                            >
                                                <Col style={{ width: 230, flex: '0 0 auto', minWidth: 0 }} gap={6}>
                                                    <span
                                                        style={{
                                                            fontSize: 14,
                                                            color: c.textPri,
                                                            fontWeight: 400,
                                                            letterSpacing: '-0.01em',
                                                        }}
                                                    >
                                                        {m.name}
                                                    </span>
                                                    <PolTag pol={m.pol} />
                                                </Col>
                                                <Muted
                                                    style={{
                                                        fontSize: 12.5,
                                                        flex: 1,
                                                        minWidth: 0,
                                                        display: '-webkit-box',
                                                        WebkitLineClamp: 2,
                                                        WebkitBoxOrient: 'vertical',
                                                        overflow: 'hidden',
                                                    }}
                                                >
                                                    {m.sub}
                                                </Muted>
                                                <Col style={{ width: 190, flex: '0 0 auto' }} gap={7}>
                                                    <Row style={{ gap: 11 }}>
                                                        <SegBarC
                                                            pct={effectiveStrength(m.pct as number, m.pol)}
                                                            hex={pz.hex}
                                                            n={10}
                                                        />
                                                        <span
                                                            style={{
                                                                fontFamily: f.mono,
                                                                fontSize: 13,
                                                                color: c.textPri,
                                                                width: 20,
                                                                textAlign: 'right',
                                                            }}
                                                        >
                                                            {Math.round(m.pct as number)}
                                                        </span>
                                                    </Row>
                                                    <Row style={{ justifyContent: 'space-between' }}>
                                                        <Meta
                                                            style={{
                                                                fontSize: 9,
                                                                letterSpacing: '0.1em',
                                                                color: pz.hex,
                                                            }}
                                                        >
                                                            {t(strengthKey(m.pct as number, m.pol))}
                                                        </Meta>
                                                        {!!m.earned && (
                                                            <Meta style={{ fontSize: 9, color: ACT }}>
                                                                +{tn('app.state.points', m.earned)}
                                                            </Meta>
                                                        )}
                                                    </Row>
                                                </Col>
                                                <Icon k="chev" size={13} color={c.subtle} />
                                            </Row>
                                        );
                                    })}
                                    <PatLegend />
                                </>
                            ) : (
                                <Muted style={{ fontSize: 12.5, padding: '12px 0' }}>
                                    {t('app.dir.patterns.none')}
                                </Muted>
                            )}
                        </Panel>
                        {leverPanel}
                    </Fluid>
                </DeskRow>
            </Appear>,
        );
    }

    return shell(
        <Appear>
            {hero}
            <div style={{ padding: '20px 20px 0' }}>{reading}</div>
            {patternsBlock}
            {trendBlock}
            {leverBlock}
        </Appear>,
    );
};

export default DirectionPage;
